/**
 * Coloana `Valută`: cursul și suma din cealaltă monedă.
 *
 * Cerut de colegele de la contabilitate: „în coloana sumă, să se poată adăuga un curs valutar și în
 * funcție de acesta să arate suma și în valută și în lei […] Să se numească VALUTA și să facă
 * conversia doar în lei sau invers în funcție de cursul valutar."
 *
 * Deci conversia merge în **ambele sensuri**: scrii cursul și vezi leii, sau scrii leii și se
 * deduce valuta. Trei lucruri de știut despre asta, toate vizibile în interfață:
 *
 *   1. **Cifra scrisă de om nu se rescrie niciodată cu una derivată.** Când scrii 5.260,00 lei, ăia
 *      rămân 5.260,00 lei în bază, iar suma în valută se calculează din curs. Conversia nu e
 *      perfect reversibilă la rotunjire, și preferăm să păstrăm ce a scris omul decât să producem
 *      o a treia valoare pe care n-a ales-o nimeni.
 *   2. **Pe RON nu apare nimic.** Un curs de 1 pe o plată în lei e zgomot care arată ca informație.
 *   3. **Fără curs nu se poate scrie suma în lei**, iar serverul o spune explicit. Un echivalent în
 *      lei calculat cu un curs ghicit e mai rău decât o celulă goală.
 */

import { formatMinor } from '@evomag/core/format';

import { AmountInput } from '../../components/AmountInput.tsx';
import { RateInput } from '../../components/RateInput.tsx';
import type { DayEntryDto } from '../../hooks/useZi.ts';

export interface PropsCelulaValuta {
  intrare: DayEntryDto;
  poateScrie: boolean;
  /** Cursul BNR al zilei, pentru avertismentul de abatere din `RateInput`. */
  referintaBnr: number | null;
  onCurs: (rateE6: number | null) => void;
  onSumaLei: (text: string) => void;
}

export function CelulaValuta({
  intrare,
  poateScrie,
  referintaBnr,
  onCurs,
  onSumaLei,
}: PropsCelulaValuta) {
  const cursE6 = intrare.planningRateE6 ?? intrare.manualRateE6 ?? null;

  // Plățile în lei: cele două coloane ar fi aceeași cifră, iar cursul ar fi mereu 1.
  if (intrare.currency === 'RON') {
    return <span className="text-ink-3">—</span>;
  }

  if (!poateScrie) {
    return (
      <span className="flex flex-col text-11">
        <span className="num text-ink-2">
          {cursE6 === null ? 'fără curs' : formatMinor(Math.round(cursE6 / 100), { minorScale: 10_000 })}
        </span>
        <span className="num text-ink-3">{formatMinor(intrare.amountRonMinor)} lei</span>
      </span>
    );
  }

  return (
    <span className="flex w-full min-w-0 flex-col gap-0.5">
      <RateInput
        value={cursE6}
        referintaBnr={referintaBnr}
        eticheta={`Curs ${intrare.currency} pentru ${intrare.supplierName}`}
        onCommit={(rateE6) => onCurs(rateE6)}
        className="w-full"
      />
      {/* Suma în lei, editabilă. Când o scrii, ea devine cifra autoritară, iar suma în valută se
          derivă din curs — invers față de calea obișnuită. */}
      <AmountInput
        value={intrare.amountRonMinor}
        moneda="lei"
        eticheta={`Suma în lei pentru ${intrare.supplierName}`}
        className="w-full"
        onCommit={(minor) => {
          if (minor === null) return;
          onSumaLei(formatMinor(minor));
        }}
      />
    </span>
  );
}
